import React, { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Search, X, ListTree } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

export const slugify = (s) =>
  String(s).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

const textOf = (children) =>
  React.Children.toArray(children).map((c) => (typeof c === "string" ? c : c?.props?.children ? textOf(c.props.children) : "")).join("");

const heading = (Tag, cls) => ({ children, ...p }) => {
  const id = slugify(textOf(children));
  return <Tag id={id} className={`scroll-mt-20 ${cls}`} {...p}>{children}</Tag>;
};

const MD = {
  h1: heading("h1", "mb-2 mt-2 font-heading text-2xl font-bold text-foreground"),
  h2: heading("h2", "mb-2 mt-8 border-b pb-1 font-heading text-lg font-bold text-foreground"),
  h3: heading("h3", "mb-1.5 mt-5 font-heading text-base font-semibold text-foreground"),
  h4: heading("h4", "mb-1 mt-4 text-sm font-semibold text-foreground"),
  p: (p) => <p className="my-2.5 text-sm leading-relaxed text-foreground/90" {...p} />,
  ul: (p) => <ul className="my-2.5 list-disc space-y-1 pl-5 text-sm leading-relaxed text-foreground/90" {...p} />,
  ol: (p) => <ol className="my-2.5 list-decimal space-y-1 pl-5 text-sm leading-relaxed text-foreground/90" {...p} />,
  li: (p) => <li className="pl-0.5" {...p} />,
  strong: (p) => <strong className="font-semibold text-foreground" {...p} />,
  a: ({ href, ...p }) => <a href={href} className="text-primary underline underline-offset-2" target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" {...p} />,
  blockquote: (p) => <blockquote className="my-3 rounded-r-md border-l-2 border-primary/50 bg-muted/40 px-3 py-2 text-sm text-muted-foreground" {...p} />,
  code: ({ inline, className, children, ...p }) =>
    className?.includes("language-") || String(children).includes("\n")
      ? <pre className="my-3 overflow-x-auto rounded-md bg-muted p-3 text-xs"><code {...p}>{children}</code></pre>
      : <code className="rounded bg-muted px-1 py-0.5 text-[0.85em]" {...p}>{children}</code>,
  hr: () => <hr className="my-5 border-border" />,
  table: (p) => <div className="my-3 overflow-x-auto rounded-md border"><table className="w-full border-collapse text-xs" {...p} /></div>,
  th: (p) => <th className="border-b border-border bg-muted px-2 py-1.5 text-left font-semibold" {...p} />,
  td: (p) => <td className="border-b border-border/60 px-2 py-1 align-top" {...p} />,
};

function parseHeadings(md) {
  const out = [];
  for (const line of md.split("\n")) {
    const m = line.match(/^(##|###)\s+(.+?)\s*$/);
    if (m) out.push({ level: m[1].length, text: m[2].replace(/[*`_]/g, ""), id: slugify(m[2].replace(/[*`_]/g, "")) });
  }
  return out;
}

function searchAll(annexes, q) {
  const needle = q.toLowerCase();
  const res = [];
  for (const a of annexes) {
    let section = a.title;
    for (const line of a.markdown.split("\n")) {
      const h = line.match(/^(##|###)\s+(.+?)\s*$/);
      if (h) { section = h[2].replace(/[*`_]/g, ""); continue; }
      if (line.toLowerCase().includes(needle)) {
        res.push({ annex: a.key, annexTitle: a.title, section, sectionId: slugify(section), line: line.replace(/[|*`#_]/g, " ").replace(/\s+/g, " ").trim().slice(0, 160) });
        if (res.length >= 40) return res;
      }
    }
  }
  return res;
}

function Highlight({ text, q }) {
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark className="rounded bg-yellow-200 px-0.5 dark:bg-yellow-700/60">{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
}

export default function AuditoriaAnexo({ annexes, activeKey, onSwitch }) {
  const [q, setQ] = useState("");
  const active = annexes.find((a) => a.key === activeKey) || annexes[0];
  const headings = useMemo(() => parseHeadings(active?.markdown || ""), [active]);
  const results = useMemo(() => (q.trim().length >= 3 ? searchAll(annexes, q.trim()) : []), [annexes, q]);

  const goTo = (key, id) => {
    const jump = () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (key !== active.key) { onSwitch(key); setTimeout(jump, 150); } else jump();
  };

  if (!active) return null;
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[260px_1fr]" data-testid={`anexo-${active.key}`}>
      <Card className="h-fit lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
        <CardContent className="p-3">
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en los anexos…" className="h-9 pl-8 pr-8" data-testid="anexo-search" />
            {q && <button type="button" onClick={() => setQ("")} className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground" data-testid="anexo-search-clear"><X className="h-4 w-4" /></button>}
          </div>
          {q.trim().length >= 3 ? (
            <div className="space-y-1" data-testid="anexo-search-results">
              <p className="px-1 text-[11px] text-muted-foreground">{results.length} resultado{results.length === 1 ? "" : "s"}{results.length >= 40 ? " (mostrando 40)" : ""}</p>
              {results.map((r, i) => (
                <button key={i} type="button" onClick={() => goTo(r.annex, r.sectionId)} data-testid={`anexo-result-${i}`}
                  className="block w-full rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted">
                  <span className="block text-[10px] font-medium uppercase tracking-wide text-primary">{r.annexTitle} · {r.section}</span>
                  <span className="block text-muted-foreground"><Highlight text={r.line} q={q.trim()} /></span>
                </button>
              ))}
            </div>
          ) : (
            <nav className="space-y-0.5" data-testid="anexo-index">
              <p className="mb-1 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><ListTree className="h-3.5 w-3.5" />Índice</p>
              {headings.map((h, i) => (
                <button key={`${h.id}-${i}`} type="button" onClick={() => goTo(active.key, h.id)} data-testid={`anexo-index-${i}`}
                  className={`block w-full truncate rounded-md py-1 text-left text-[13px] hover:bg-muted ${h.level === 3 ? "pl-5 text-muted-foreground" : "px-2 font-medium"}`}>
                  {h.text}
                </button>
              ))}
            </nav>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-5 sm:p-8">
          <p className="mb-4 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-foreground/80" data-testid="anexo-intro">{active.intro}</p>
          <article className="max-w-none" data-testid="anexo-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{active.markdown}</ReactMarkdown>
          </article>
        </CardContent>
      </Card>
    </div>
  );
}
